import { apiClient } from './client'

// ============================================================================
// Types
// ============================================================================

export interface Announcement {
  announcementId: number
  title: string
  content: string
  contentFormat: string
  status: string
  isPinned: boolean
  isGlobal: boolean
  targetAppTypes?: string[] | null
  targetOrganizationIds?: number[] | null
  publishStartAt: string | null
  publishEndAt: string | null
  viewCount: number
  authorId: number
  authorName: string
  source: string
  sourceOrganizationId?: number | null
  isRead?: boolean
  createdAt: string
  updatedAt: string
}

export interface AnnouncementListResponse {
  announcements: Announcement[]
  totalCount: number
  page: number
  size: number
  totalPages: number
}

export type AnnouncementScope = 'all' | 'global' | 'center'
export type AnnouncementCategory = 'all' | 'pinned' | 'normal'
export type AnnouncementSort = 'pinned' | 'latest'

export interface UnreadCountResponse {
  unreadCount: number
}

export interface OrgAnnouncementRequest {
  title: string
  content: string
  contentFormat?: string
  status?: string
  isPinned?: boolean
}

interface ApiResponse<T> {
  success: boolean
  data: T
}

// ============================================================================
// B2B 읽기 API (학습자 + 관리자 공통)
// ============================================================================

export const announcementApi = {
  getList: async (params: {
    page?: number
    size?: number
    keyword?: string
    scope?: AnnouncementScope
    category?: AnnouncementCategory
    sort?: AnnouncementSort
  } = {}): Promise<AnnouncementListResponse> => {
    const response = await apiClient.get<ApiResponse<AnnouncementListResponse>>(
      '/b2b/announcements',
      { params }
    )
    if (!response.data.data) {
      throw new Error('Failed to fetch announcements')
    }
    return response.data.data
  },

  getById: async (id: number): Promise<Announcement> => {
    const response = await apiClient.get<ApiResponse<Announcement>>(
      `/b2b/announcements/${id}`
    )
    if (!response.data.data) {
      throw new Error('Announcement not found')
    }
    return response.data.data
  },

  getUnreadCount: async (): Promise<UnreadCountResponse> => {
    const response = await apiClient.get<ApiResponse<UnreadCountResponse>>(
      '/b2b/announcements/unread-count'
    )
    if (!response.data.data) {
      return { unreadCount: 0 }
    }
    return response.data.data
  },
}

// ============================================================================
// 조직 관리자 관리 API (LC 관리자 전용)
// ============================================================================

export const orgAnnouncementApi = {
  getList: async (params: {
    page?: number
    size?: number
    status?: string
    keyword?: string
  } = {}): Promise<AnnouncementListResponse> => {
    const response = await apiClient.get<ApiResponse<AnnouncementListResponse>>(
      '/b2b/announcements/manage',
      { params }
    )
    if (!response.data.data) {
      throw new Error('Failed to fetch org announcements')
    }
    return response.data.data
  },

  create: async (data: OrgAnnouncementRequest): Promise<Announcement> => {
    const response = await apiClient.post<ApiResponse<Announcement>>(
      '/b2b/announcements/manage',
      data
    )
    if (!response.data.data) {
      throw new Error('Failed to create announcement')
    }
    return response.data.data
  },

  update: async (id: number, data: OrgAnnouncementRequest): Promise<Announcement> => {
    const response = await apiClient.put<ApiResponse<Announcement>>(
      `/b2b/announcements/manage/${id}`,
      data
    )
    if (!response.data.data) {
      throw new Error('Failed to update announcement')
    }
    return response.data.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/b2b/announcements/manage/${id}`)
  },

  publish: async (id: number): Promise<Announcement> => {
    const response = await apiClient.put<ApiResponse<Announcement>>(
      `/b2b/announcements/manage/${id}/publish`
    )
    if (!response.data.data) {
      throw new Error('Failed to publish announcement')
    }
    return response.data.data
  },
}
